import {
  IHealthScribeAudioProvider,
  IAudioChunk,
  AudioRecordingMode,
} from "../../../core/ports/HealthScribe";
import { AudioAGC } from "../../../../util/audioAGC";
import { getAndroidAudioVersion } from "../../../../util/androidBridge";

/**
 * HealthScribeAudioProvider - Handles audio capture for both AWS and Heidi transcription modes.
 *
 * ## Audio Recording Modes
 *
 * The `mode` parameter in `startRecording(mode)` controls the sample rate:
 * - "aws" mode: 16kHz sample rate (required by AWS Transcribe Medical backend)
 * - "heidi" mode: 44.1kHz sample rate (higher quality for Heidi's transcription service)
 *
 * ## Where modes are set (explicit calls, no defaults relied upon):
 * - AWS mode: Set in healthScribeServiceImpl.ts → startAudioRecording() → startRecording("aws")
 * - Heidi mode: Set in healthScribeController.ts → startOrResumeRecordingV2() → startRecording("heidi")
 *
 * ## Audio Processing Paths
 *
 * Two parallel audio paths are set up from the microphone source:
 *
 * ### Path 1: PCM Streaming (AWS Mode Only)
 * ```
 * source → scriptProcessor (with AGC) → onAudioChunk callback → WebSocket → AWS Transcribe
 * ```
 * - Used for: Real-time live transcription in AWS mode
 * - Processing: AGC (Automatic Gain Control) normalizes speech levels
 * - Output: 16-bit PCM chunks sent via WebSocket for live transcription
 * - In Heidi mode: Path exists but onAudioChunk callback is never set, so chunks go nowhere
 *
 * ### Path 2: Complete Audio Recording (Both Modes)
 * ```
 * source → completeAudioGainNode (2x boost) → completeAudioDestination → MediaRecorder → Blob
 * ```
 * - Used for: Complete audio file that gets uploaded after recording stops
 * - Processing: Static 2x gain boost for speech clarity
 * - Output: WebM/MP4/WAV audio blob
 * - AWS mode: Uploaded to S3 as backup
 * - Heidi mode: Uploaded to Heidi backend for transcription
 */
export class HealthScribeAudioProvider implements IHealthScribeAudioProvider {
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private audioLevelInterval: number | null = null;
  private isRecordingState = false;
  private isPausedState = false;
  private currentAudioLevel = 0;

  // ============================================================================
  // PATH 1: PCM Streaming for AWS Real-Time Transcription
  // ============================================================================
  // ScriptProcessor captures raw audio and applies AGC before sending to WebSocket.
  // This path is ONLY used in AWS mode - in Heidi mode, onAudioChunk is never set.
  private scriptProcessor: ScriptProcessorNode | null = null;
  // AGC (Automatic Gain Control) normalizes speech volume levels for consistent transcription.
  // Only affects PCM chunks sent to AWS WebSocket, not the complete audio recording.
  private agcProcessor: AudioAGC | null = null;

  // ============================================================================
  // PATH 2: Complete Audio Recording (Used by BOTH AWS and Heidi modes)
  // ============================================================================
  // MediaRecorder captures the complete audio session as a single file.
  // - AWS mode: Uploaded to S3 as backup after recording
  // - Heidi mode: Uploaded to Heidi backend for transcription
  private completeAudioRecorder: MediaRecorder | null = null;
  private completeAudioBlob: Blob | null = null;
  private recordingStartTime: number = 0;
  // Destination node that MediaRecorder records from
  private completeAudioDestination: MediaStreamAudioDestinationNode | null =
    null;
  // Static 2x gain boost (~6dB) for better speech clarity in the complete recording
  private completeAudioGainNode: GainNode | null = null;

  // ============================================================================
  // Callbacks
  // ============================================================================
  // onAudioChunk: Set by service layer in AWS mode to send PCM chunks to WebSocket.
  //               In Heidi mode, this remains null (controller skips service layer setup).
  public onAudioChunk: ((chunk: IAudioChunk) => void) | null = null;
  public onAudioLevel: ((level: number) => void) | null = null;
  public onError: ((error: string) => void) | null = null;

  // Audio chunks accumulator (for continuous recording - single chunk on stop)
  private audioChunks: Blob[] = [];
  private androidRecordingId: string | null = null;

  /**
   * @deprecated window.AndroidAudio is deprecated and no longer used. Do not add new usages.
   */
  private isAndroidAudioBridgeAvailable(): boolean {
    return (
      typeof window !== "undefined" &&
      !!window.AndroidAudio &&
      typeof window.AndroidAudio.startRecording === "function" &&
      typeof window.AndroidAudio.stopRecording === "function" &&
      getAndroidAudioVersion() === 1
    );
  }

  private emitAndroidAudioToast(message: string): void {
    if (typeof window === "undefined") {
      return;
    }
    window.dispatchEvent(
      new CustomEvent("android-audio-toast", {
        detail: { message, type: "error" },
      })
    );
  }

  async requestMicrophonePermission(): Promise<{
    granted: boolean;
    error?: string;
  }> {
    try {
      if (this.isAndroidAudioBridgeAvailable()) {
        // console.log(
        //   "[HealthScribe Audio Provider] AndroidAudio bridge available - skipping browser mic permission"
        // );
        return { granted: true };
      }

      // Check if permissions API is available
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        if (permission.state === "granted") {
          return { granted: true };
        } else if (permission.state === "denied") {
          return { granted: false, error: "Microphone permission denied" };
        }
      }

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop()); // Stop the test stream
      return { granted: true };
    } catch (error) {
      console.error(
        "[HealthScribe Audio Provider] Microphone permission error:",
        error
      );
      return {
        granted: false,
        error: `Failed to get microphone permission: ${error}`,
      };
    }
  }

  /**
   * Start audio recording with the specified mode.
   *
   * @param mode - MUST be explicitly set by caller (do not rely on default):
   *   - "aws": 16kHz sample rate, set in healthScribeServiceImpl.ts for AWS Transcribe
   *   - "heidi": 44.1kHz sample rate, set in healthScribeController.ts for Heidi transcription
   */
  async startRecording(
    mode: AudioRecordingMode = "aws"
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // IDEMPOTENT: If already recording, return success
      // This is critical for Heidi mode where controller starts recording,
      // and then component calls startAudioRecording() again
      if (this.isRecordingState) {
        return { success: true };
      }

      if (this.isAndroidAudioBridgeAvailable()) {
        const recordingId = window.AndroidAudio!.startRecording();
        if (!recordingId) {
          this.emitAndroidAudioToast("Failed to start Android audio recording");
          return {
            success: false,
            error: "Failed to start Android audio recording.",
          };
        }

        this.androidRecordingId = recordingId;
        this.recordingStartTime = Date.now();
        this.isRecordingState = true;
        this.isPausedState = false;
        return { success: true };
      }

      // Sample rate depends on mode:
      // - AWS mode: 16000 Hz (required by AWS Transcribe Medical, backend hardcodes this in aws_service.py)
      // - Heidi mode: 44100 Hz (higher quality for Heidi's transcription service)
      const sampleRate = mode === "heidi" ? 44100 : 16000;

      // Get microphone stream
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false, // Echo cancellation off is important for Sarah's use case of telehealth. If it's on, the browser mutes audio coming from the speaker (the telehealth patient).
          noiseSuppression: true,
          autoGainControl: false, // Disabled - using custom AGC implementation
          sampleRate: sampleRate,
          channelCount: 1, // Mono audio
        },
      });

      // Set up Web Audio API for raw PCM data capture (AWS Transcribe Medical compatible)
      this.audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)({
        sampleRate: sampleRate,
        latencyHint: "interactive",
      });

      // Create MediaStreamSource from microphone
      this.source = this.audioContext.createMediaStreamSource(this.mediaStream);

      // ========================================================================
      // PATH 1 SETUP: PCM Streaming for AWS Real-Time Transcription
      // ========================================================================
      // This path captures raw PCM audio, applies AGC, and sends chunks via onAudioChunk.
      // - AWS mode: onAudioChunk is set by service layer → chunks go to WebSocket
      // - Heidi mode: onAudioChunk remains null → chunks are generated but discarded
      const bufferSize = 4096;
      this.scriptProcessor = this.audioContext.createScriptProcessor(
        bufferSize,
        1,
        1
      );

      // Connect audio graph: source -> scriptProcessor -> destination (silent output)
      this.source.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);

      // Initialize AGC (Automatic Gain Control) for speech normalization
      // Only affects PCM chunks (PATH 1), not the complete audio recording (PATH 2)
      this.agcProcessor = new AudioAGC(this.audioContext.sampleRate);

      // Handle audio processing to capture raw PCM data (PATH 1)
      // This generates AGC-processed PCM chunks for AWS WebSocket streaming.
      // In Heidi mode, onAudioChunk is null so chunks are generated but not sent anywhere.
      this.scriptProcessor.onaudioprocess = (event) => {
        if (!this.isRecordingState || this.isPausedState) {
          return;
        }

        try {
          const inputBuffer = event.inputBuffer;
          const channelData = inputBuffer.getChannelData(0); // Get mono channel

          // Apply AGC (Automatic Gain Control) for speech normalization
          const processedData = this.agcProcessor
            ? this.agcProcessor.process(channelData)
            : channelData;

          // Convert Float32Array to Int16Array (16-bit PCM for AWS)
          const pcmData = new Int16Array(processedData.length);
          for (let i = 0; i < processedData.length; i++) {
            // Convert from [-1, 1] float to [-32768, 32767] int16
            const clamped = Math.max(-1, Math.min(1, processedData[i]));
            pcmData[i] = clamped * 32767;
          }

          // Create binary blob from PCM data (AWS Transcribe Medical compatible)
          const pcmBlob = new Blob([pcmData.buffer], { type: "audio/pcm" });

          if (this.onAudioChunk && pcmBlob.size > 0) {
            const chunk: IAudioChunk = {
              data: pcmBlob,
              timestamp: Date.now(),
              duration:
                (inputBuffer.length / this.audioContext!.sampleRate) * 1000, // Duration in ms
            };
            this.onAudioChunk(chunk);
          }
        } catch (error) {
          console.error(
            "🎤 [HealthScribe Audio Provider] Error processing audio chunk:",
            error
          );

          // Notify recovery service immediately - audio processing failure is critical
          // Recovery service has its own 2-second cooldown to prevent spam
          if (this.onError) {
            this.onError(`Audio processing failure: ${error}`);
          }

          // Don't throw - continue processing next chunks
        }
      };

      // Set up complete audio recording MediaRecorder
      // Try formats in order of preference, with fallbacks for Safari/iPad
      let mimeType = "audio/webm";
      if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        mimeType = "audio/webm;codecs=opus";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4"; // Safari/iPad support
      } else if (MediaRecorder.isTypeSupported("audio/wav")) {
        mimeType = "audio/wav"; // Fallback to WAV
      } else {
        console.warn(
          "[HealthScribe Audio Provider] No preferred audio format supported, using default:",
          mimeType
        );
      }

      // ========================================================================
      // PATH 2 SETUP: Complete Audio Recording (used by BOTH AWS and Heidi modes)
      // ========================================================================
      // Create gain node with 2x static boost for better speech clarity
      this.completeAudioGainNode = this.audioContext.createGain();
      this.completeAudioGainNode.gain.value = 2.0; // 2x gain (~6dB boost)

      // Create MediaStreamDestination for MediaRecorder to record from
      // Audio path: source → completeAudioGainNode → completeAudioDestination
      this.completeAudioDestination =
        this.audioContext.createMediaStreamDestination();
      this.source.connect(this.completeAudioGainNode);
      this.completeAudioGainNode.connect(this.completeAudioDestination);

      // Create MediaRecorder for complete audio file recording
      // 192 kbps is high quality for speech recordings
      // Note: Safari may not respect the bitrate setting
      this.completeAudioRecorder = new MediaRecorder(
        this.completeAudioDestination.stream,
        {
          mimeType,
          audioBitsPerSecond: 192000, // 192 kbps - high quality
        }
      );

      // Reset state
      this.completeAudioBlob = null;
      this.recordingStartTime = Date.now();

      // Reset audio chunks array
      this.audioChunks = [];

      // Handle data available event - accumulate chunks for final blob
      // CONTINUOUS RECORDING: We only get one chunk when stop() is called
      // This avoids the multi-header corruption issue with timeslice
      this.completeAudioRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      // Handle recording errors
      this.completeAudioRecorder.onerror = (event) => {
        console.error(
          "[HealthScribe Audio Provider] Complete audio recorder error:",
          event
        );
        if (this.onError) {
          this.onError(`Complete audio recorder error: ${event}`);
        }
      };

      // Handle recording stop event
      this.completeAudioRecorder.onstop = () => {
        // Combine all chunks into final blob (typically just one chunk with continuous recording)
        if (this.audioChunks.length > 0) {
          this.completeAudioBlob = new Blob(this.audioChunks, {
            type: mimeType,
          });
        }
      };

      // Start CONTINUOUS recording (no timeslice)
      // This produces a single valid audio file with proper headers
      // Avoids the multi-header corruption issue that occurred with timeslice
      this.completeAudioRecorder.start();

      this.isRecordingState = true;
      this.isPausedState = false;

      // Log recording state for debugging

      // Set up audio level monitoring
      this.setupAudioLevelMonitoring();

      return { success: true };
    } catch (error) {
      console.error(
        "[HealthScribe Audio Provider] Failed to start recording:",
        error
      );

      // Notify recovery service of critical error
      if (this.onError) {
        this.onError(`Failed to start recording: ${error}`);
      }

      return { success: false, error: `Failed to start recording: ${error}` };
    }
  }

  async stopRecording(): Promise<{ audioBlob: Blob; duration: number }> {
    if (this.isAndroidAudioBridgeAvailable() && this.androidRecordingId) {
      const recordingId = this.androidRecordingId;
      this.androidRecordingId = null;
      const url = window.AndroidAudio!.stopRecording(recordingId);
      if (!url) {
        this.emitAndroidAudioToast("Failed to stop Android audio recording");
        this.cleanup();
        throw new Error("Failed to stop Android audio recording.");
      }

      const response = await fetch(url, { method: "GET" });
      if (!response.ok) {
        this.emitAndroidAudioToast(
          `Failed to fetch Android audio recording (${response.status})`
        );
        this.cleanup();
        throw new Error(
          `Failed to fetch Android audio recording: ${response.status}`
        );
      }

      const audioBlob = await response.blob();
      const duration =
        this.recordingStartTime > 0 ? Date.now() - this.recordingStartTime : 0;

      this.cleanup();
      return { audioBlob, duration };
    }

    return new Promise((resolve, reject) => {
      // Enhanced error logging for debugging
      if (!this.completeAudioRecorder || !this.isRecordingState) {
        const errorDetails = {
          hasRecorder: !!this.completeAudioRecorder,
          isRecording: this.isRecordingState,
          isPaused: this.isPausedState,
          recorderState: this.completeAudioRecorder?.state,
        };
        console.warn(
          "⏸️🎤 [Audio Provider] Stop called but no active recording - returning empty audio (defensive behavior)",
          errorDetails
        );

        // DEFENSIVE: Instead of rejecting (which causes errors), return empty audio
        // This allows the pause/stop flow to complete gracefully even in state mismatch scenarios
        // The caller can check the blob size to determine if actual audio was recorded
        const emptyBlob = new Blob([], { type: "audio/webm" });
        const duration =
          this.recordingStartTime > 0
            ? Date.now() - this.recordingStartTime
            : 0;

        // Clean up any partial state
        this.cleanup();

        resolve({ audioBlob: emptyBlob, duration });
        return;
      }

      const recordingDuration = Date.now() - this.recordingStartTime;

      // Set up stop handler for complete audio recorder
      this.completeAudioRecorder.onstop = () => {
        // Wait a moment for ondataavailable to be called
        setTimeout(() => {
          // In chunked mode, combine all chunks into final blob
          if (this.audioChunks.length > 0 && !this.completeAudioBlob) {
            const mimeType =
              this.completeAudioRecorder?.mimeType || "audio/webm";
            this.completeAudioBlob = new Blob(this.audioChunks, {
              type: mimeType,
            });
          }

          if (this.completeAudioBlob) {
            const audioBlob = this.completeAudioBlob;

            // Clean up resources
            this.cleanup();

            resolve({ audioBlob, duration: recordingDuration });
          } else {
            console.error(
              "[HealthScribe Audio Provider] No audio blob available after recording stopped"
            );
            this.cleanup();
            reject(new Error("No audio data recorded"));
          }
        }, 10); // Small delay to ensure ondataavailable is called
      };

      // Stop the complete audio recording
      this.completeAudioRecorder.stop();
    });
  }

  async pauseRecording(): Promise<void> {
    // Idempotent: If already paused, just return success
    if (this.isPausedState) {
      return;
    }

    if (this.isAndroidAudioBridgeAvailable() && this.androidRecordingId) {
      const success = window.AndroidAudio?.pauseRecording?.();
      if (!success) {
        this.emitAndroidAudioToast("Failed to pause Android audio recording");
        throw new Error("Failed to pause Android audio recording");
      }
      this.isPausedState = true;
      return;
    }

    if (!this.completeAudioRecorder || !this.isRecordingState) {
      const errorMsg = "No active recording to pause";

      // Notify recovery service of error
      if (this.onError) {
        this.onError(errorMsg);
      }

      throw new Error(errorMsg);
    }

    // Pause both PCM processing and complete audio recording
    this.completeAudioRecorder.pause();
    this.isPausedState = true;
  }

  async resumeRecording(): Promise<void> {
    // Idempotent: If already recording (not paused), just return success
    if (!this.isPausedState && this.isRecordingState) {
      return;
    }

    if (this.isAndroidAudioBridgeAvailable() && this.androidRecordingId) {
      const success = window.AndroidAudio?.resumeRecording?.();
      if (!success) {
        this.emitAndroidAudioToast("Failed to resume Android audio recording");
        throw new Error("Failed to resume Android audio recording");
      }
      this.isPausedState = false;
      return;
    }

    if (!this.completeAudioRecorder || !this.isRecordingState) {
      const errorMsg = "No active recording to resume";

      // Notify recovery service of error
      if (this.onError) {
        this.onError(errorMsg);
      }

      throw new Error(errorMsg);
    }

    if (!this.isPausedState) {
      // This shouldn't happen due to check above, but keep for safety

      return;
    }

    // Resume both PCM processing and complete audio recording
    this.completeAudioRecorder.resume();
    this.isPausedState = false;
  }

  isRecording(): boolean {
    return this.isRecordingState;
  }

  isPaused(): boolean {
    return this.isPausedState;
  }

  getCurrentStream(): MediaStream | null {
    return this.mediaStream;
  }

  getAudioLevel(): number {
    return this.currentAudioLevel;
  }

  /**
   * Get the current recording duration in milliseconds
   */
  getRecordingDuration(): number {
    if (!this.isRecordingState || this.recordingStartTime === 0) {
      return 0;
    }
    return Date.now() - this.recordingStartTime;
  }

  /**
   * Get information about the current recording session
   */
  getRecordingInfo(): {
    isRecording: boolean;
    isPaused: boolean;
    duration: number;
    hasCompleteAudio: boolean;
    mimeType: string | null;
  } {
    return {
      isRecording: this.isRecordingState,
      isPaused: this.isPausedState,
      duration: this.getRecordingDuration(),
      hasCompleteAudio: this.completeAudioBlob !== null,
      mimeType: this.completeAudioRecorder?.mimeType || null,
    };
  }

  /**
   * Get AGC (Automatic Gain Control) statistics for monitoring
   */
  getAGCStats(): {
    enabled: boolean;
    currentGain: number;
    currentGainDb: number;
    currentRMS: number;
  } | null {
    if (!this.agcProcessor) {
      return null;
    }

    return {
      enabled: true,
      currentGain: this.agcProcessor.getCurrentGain(),
      currentGainDb: this.agcProcessor.getCurrentGainDb(),
      currentRMS: this.agcProcessor.getCurrentRMS(),
    };
  }

  private setupAudioLevelMonitoring(): void {
    if (!this.audioContext || !this.source) {
      console.warn(
        "[HealthScribe Audio Provider] AudioContext or source not available for level monitoring"
      );
      return;
    }

    try {
      // Create analyser node and connect to existing audio graph
      this.analyser = this.audioContext.createAnalyser();

      // Configure analyser
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.8;
      this.analyser.minDecibels = -90;
      this.analyser.maxDecibels = -10;

      // Connect source to analyser (in addition to existing connections)
      this.source.connect(this.analyser);

      // Start monitoring
      let lastReportedLevel = 0;
      let lastReportTime = Date.now();
      this.audioLevelInterval = window.setInterval(() => {
        if (this.analyser && this.onAudioLevel) {
          const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
          this.analyser.getByteFrequencyData(dataArray);

          // Calculate average level
          const average =
            dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
          this.currentAudioLevel = average;

          const now = Date.now();
          const timeSinceLastReport = now - lastReportTime;
          const levelChanged = Math.abs(average - lastReportedLevel) >= 5;
          const needsHeartbeat = timeSinceLastReport >= 2000; // Send heartbeat every 2s

          // Notify if: level changed significantly OR we need a heartbeat (for recovery service)
          if (levelChanged || needsHeartbeat) {
            lastReportedLevel = average;
            lastReportTime = now;
            this.onAudioLevel(average);
          }
        }
      }, 100); // Update every 100ms
    } catch (error) {
      console.error(
        "[HealthScribe Audio Provider] Failed to setup audio level monitoring:",
        error
      );

      // Notify recovery service of setup error (non-critical, monitoring will be disabled)
      if (this.onError) {
        this.onError(`Failed to setup audio level monitoring: ${error}`);
      }
    }
  }

  private cleanup(): void {
    // Stop audio level monitoring
    if (this.audioLevelInterval) {
      window.clearInterval(this.audioLevelInterval);
      this.audioLevelInterval = null;
    }

    // Clean up AGC processor
    if (this.agcProcessor) {
      this.agcProcessor.reset();
      this.agcProcessor = null;
    }

    // Disconnect and clean up Web Audio API nodes
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor.onaudioprocess = null;
      this.scriptProcessor = null;
    }

    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }

    // Clean up complete audio gain node (PATH 2)
    if (this.completeAudioGainNode) {
      this.completeAudioGainNode.disconnect();
      this.completeAudioGainNode = null;
    }

    // Clean up complete audio destination node (PATH 2)
    if (this.completeAudioDestination) {
      this.completeAudioDestination.disconnect();
      this.completeAudioDestination = null;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    // Stop media stream
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    // Close audio context
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Clean up complete audio recorder
    if (this.completeAudioRecorder) {
      this.completeAudioRecorder.onstop = null;
      this.completeAudioRecorder.ondataavailable = null;
      this.completeAudioRecorder.onerror = null;
      this.completeAudioRecorder = null;
    }

    // Reset state
    this.mediaRecorder = null;
    this.isRecordingState = false;
    this.isPausedState = false;
    this.currentAudioLevel = 0;
    this.completeAudioBlob = null;
    this.recordingStartTime = 0;
    this.audioChunks = [];
    this.androidRecordingId = null;
  }
}
